import { Accordion, Anchor } from '@mantine/core';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';

import { shipmentSummarySectionMessages } from './shipment-summary-section-messages';
import styles from './ShipmentSummarySection.module.css';
import { resolveChargeLabel, selectShipmentCharges } from '../../../../ltl/charge-labels';
import { DetailSection } from '../detail-section/DetailSection';

import type { LtlShipment } from '../../../../types/ltl';

type ShipmentSummarySectionProps = {
  shipment: LtlShipment;
  currencyCode?: string;
};

const DEFAULT_CURRENCY_CODE = 'USD';

// Local copy of the FreightRateCard initials recipe (not exported there).
const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : isoDate;
};

/**
 * Right-column Shipment Summary card (§6.6 / §7.6). Carrier row with an
 * initials logo box, a Cost Breakdown accordion (formatted total + LTL
 * disclaimer), and a static Invoice details block. Store-agnostic.
 */
export const ShipmentSummarySection = ({ shipment, currencyCode = DEFAULT_CURRENCY_CODE }: ShipmentSummarySectionProps) => {
  const intl = useIntl();

  const arrivesDate = formatDate(shipment.estimatedDeliveryDate);
  const formattedTotal = intl.formatNumber(shipment.totalCharges, { style: 'currency', currency: currencyCode });
  const charges = selectShipmentCharges(shipment);

  return (
    <DetailSection testId='shipment-summary-section' title={intl.formatMessage(shipmentSummarySectionMessages.sectionTitle)}>
      <div className={styles.carrierRow}>
        <div className={styles.logoBox}>
          <span className={styles.logoInitials}>{getInitials(shipment.carrierName)}</span>
        </div>
        <div className={styles.carrierInfo}>
          <span className={styles.carrierName}>
            {intl.formatMessage(shipmentSummarySectionMessages.carrierService, {
              carrier: shipment.carrierName,
              service: shipment.serviceName,
            })}
          </span>
          {arrivesDate !== '' && (
            <span className={styles.arrives}>{intl.formatMessage(shipmentSummarySectionMessages.arrives, { date: arrivesDate })}</span>
          )}
        </div>
      </div>

      <Accordion variant='contained'>
        <Accordion.Item value='cost-breakdown'>
          <Accordion.Control>{intl.formatMessage(shipmentSummarySectionMessages.costBreakdown)}</Accordion.Control>
          <Accordion.Panel>
            {charges.length > 0 && (
              <div className={styles.chargesList}>
                {charges.map((charge, index) => (
                  <div className={styles.chargeRow} data-testid={`cost-breakdown-charge-${charge.code}`} key={`${charge.code}-${index}`}>
                    <span className={styles.chargeLabel}>{resolveChargeLabel(charge, intl)}</span>
                    <span className={styles.chargeAmount}>
                      {intl.formatNumber(charge.price, { style: 'currency', currency: currencyCode })}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>{intl.formatMessage(shipmentSummarySectionMessages.total)}</span>
              <span className={styles.totalAmount}>{formattedTotal}</span>
            </div>
            <span className={styles.disclaimer}>{intl.formatMessage(shipmentSummarySectionMessages.costDisclaimer)}</span>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <div className={styles.invoice}>
        <span className={styles.invoiceCopy}>{intl.formatMessage(shipmentSummarySectionMessages.invoiceCopy)}</span>
        {/* Placeholder href: real billing destination is wired by the host later. */}
        <Anchor className={styles.manageBilling} href='#' rel='noopener noreferrer' target='_blank'>
          {intl.formatMessage(shipmentSummarySectionMessages.manageBilling)}
        </Anchor>
      </div>
    </DetailSection>
  );
};
